'use client';
import { BoatBySlugQueryResult } from '@app/sanity.types';
import SquareAd from '@components/Advertisements/SquareAd';
import { BoatDocuments } from '@components/BoatDocuments';
import { useViewerPlan } from '@hooks/useViewerPlan';
import styles from '@styles/SingleBoat/index.module.scss';
import { getBoatName } from '@utils/getBoatName';
import { Alert } from 'antd';
import { PortableText, type PortableTextComponents } from 'next-sanity';
import { AntMessageProvider, AntNotificationProvider } from 'providers';
import { FC, useState } from 'react';
import BoatDescription from './BoatDescription/BoatDescription';
import BoatOverview from './BoatOverview';
import { BoatOverviewMenu, BoatTable as boatTable } from './boatMenus';
import DetailTable from './DetailTable';
import Jumbotron from './Jumbotron';
import OverviewAnchor from './OverviewAnchor';

type Props = {
  boat: BoatBySlugQueryResult;
};

/**
 * `notes` is a `blockContent` (Portable Text) field, so it has to be rendered as blocks — it was
 * previously interpolated into a template string and printed as `[object Object]`.
 *
 * Only the `href` variant of the `link` annotation is linkified: the boat query does not deref
 * annotation targets, so a `page` link carries a bare `_ref` and is rendered as plain text.
 */
const notesComponents: PortableTextComponents = {
  block: {
    normal: ({ children }) => <p className={styles.notes__paragraph}>{children}</p>,
  },
  marks: {
    link: ({ value, children }) => {
      const href = typeof value?.href === 'string' ? value.href : undefined;

      if (!href) return <>{children}</>;

      return (
        <a href={href} target={value?.openInNewTab ? '_blank' : undefined} rel="noopener noreferrer">
          {children}
        </a>
      );
    },
  },
};

const SingleBoat: FC<Props> = ({ boat }) => {
  const { isSubscribed } = useViewerPlan();

  const [jumbotronHeight, setJumbotronHeight] = useState<number>(192);
  const boatTitle = getBoatName(boat);
  const detailTable = boatTable(boat);
  const safeDetailTable = detailTable.filter((item) => {
    return (
      !!item &&
      typeof item.title === 'string' &&
      typeof item.link === 'string' &&
      Array.isArray(item.menu) &&
      item.menu.every((menuItem) => !!menuItem && typeof menuItem.link === 'string')
    );
  });

  return (
    <>
      <Jumbotron setJumbotronHeight={setJumbotronHeight} boat={boat} />
      <BoatOverview
        boatName={boatTitle}
        image={boat?.image}
        files={boat?.files}
        menuItems={BoatOverviewMenu(boat, jumbotronHeight)}
      />
      {boat.enableDescription && boat?.description && <BoatDescription description={boat?.description} />}
      {!!boat.documents?.length && <BoatDocuments documents={boat.documents} />}

      <section className={styles.container} id="single-public-boat-content">
        <div className={styles.container__content}>
          <div className={styles.container__content__sticky}>
            <OverviewAnchor details={safeDetailTable} jumbotronHeight={jumbotronHeight} />
            {!isSubscribed ? <SquareAd /> : null}
          </div>
        </div>
        <div className={styles.container__tables}>
          {safeDetailTable.map((items) => (
            <DetailTable key={items.title} {...items} />
          ))}

          {boat.notes?.length ? (
            <Alert
              message="Notes"
              description={<PortableText value={boat.notes} components={notesComponents} />}
              type="info"
              showIcon
              className={styles.notes}
            />
          ) : null}
        </div>
      </section>
    </>
  );
};

const SingleBoatPage: FC<Props> = ({ boat }) => {
  return (
    <AntMessageProvider>
      <AntNotificationProvider>
        <SingleBoat boat={boat} />
      </AntNotificationProvider>
    </AntMessageProvider>
  );
};

export default SingleBoatPage;
